import React, { useState } from 'react';
import { Spin } from 'antd';
import {
  CheckOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  RocketOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AddBranchIndex from './AddBranch';
import { type BranchResponse } from './addBranchApi';
import './index.scss';
import './FirstBranch.scss';

const LOADING_TEXT = 'Setting up your workspace...';

interface FirstBranchProps {
  onDone: () => void;
}

const STEPS: Array<{
  title: string;
  description: string;
  state: 'done' | 'current' | 'todo';
  icon: JSX.Element;
}> = [
  {
    title: 'Account created',
    description: 'Your organization and admin account are ready.',
    state: 'done',
    icon: <UserOutlined />,
  },
  {
    title: 'Add your branch',
    description:
      'Add your first branch so you can start recording customers, pledges and loans.',
    state: 'current',
    icon: <ShopOutlined />,
  },
  {
    title: 'Start using Gold Pulse',
    description:
      'Track loans, interest, deposits and reports in one place. More branches can be added later from Settings.',
    state: 'todo',
    icon: <RocketOutlined />,
  },
];

// Shown instead of the app (no sidebar, no module routes) while the organization has no branch yet
export default function FirstBranchIndex({
  onDone,
}: FirstBranchProps): JSX.Element {
  // After the branch is saved, show a short loader before the dashboard opens
  const [isFinishing, setIsFinishing] = useState(false);

  const handleAdded = (branch?: BranchResponse) => {
    // The login response had branch: null; keep the new branch as the session's branch
    const current = window.localStorage.getItem('branch');
    if (branch !== undefined && (current === null || current === 'null')) {
      window.localStorage.setItem(
        'branch',
        JSON.stringify({ ...branch, id: branch._id })
      );
    }
    setIsFinishing(true);
    window.setTimeout(onDone, 1500);
  };

  const navigate = useNavigate();

  const signOut = () => {
    window.localStorage.clear();
    navigate('/sign-in');
  };

  const orgName = window.localStorage.getItem('organizationName') ?? '';

  return (
    <div className="settings__page onboarding">
      <div className="onboarding__shell">
        <Spin spinning={isFinishing} fullscreen tip={LOADING_TEXT} />
        <aside className="onboarding__side">
          <p className="onboarding__intro">
            <InfoCircleOutlined />
            {orgName !== ''
              ? ` Welcome, ${orgName}. Let's finish setting up your organization.`
              : " Let's finish setting up your organization."}
          </p>

          <ol className="onboarding__steps" aria-label="Setup progress">
            {STEPS.map((step) => (
              <li
                key={step.title}
                className={`onboarding__step onboarding__step--${step.state}`}
              >
                <span className="onboarding__dot">
                  {step.state === 'done' ? <CheckOutlined /> : step.icon}
                </span>
                <div className="onboarding__text">
                  <p className="onboarding__step-title">{step.title}</p>
                  <p className="onboarding__step-desc">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>

          <button
            type="button"
            className="onboarding__signout"
            onClick={signOut}
          >
            <LogoutOutlined /> Sign out
          </button>
        </aside>

        <section className="onboarding__main">
          <p className="onboarding__eyebrow">STEP 2 OF 3</p>
          <h1 className="onboarding__title">Add your first branch</h1>
          <p className="onboarding__subtitle">
            Enter the details of your main branch. They appear on your loans,
            receipts and reports.
          </p>

          <AddBranchIndex
            onAdded={handleAdded}
            submitLabel="Save and continue"
            loadingTip={LOADING_TEXT}
          />
        </section>
      </div>
    </div>
  );
}
